import { useMemo } from 'react';

import { Row } from '@tanstack/react-table';
import { useFormContext, useWatch } from 'react-hook-form';

import { parseFormNumber } from '@/helpers/finance';

import {
  getBillFieldName,
  isNonDataSubRow,
} from '../../components/generate-bills-table/bill-row.helpers';
import { GenerateBillListItem, GenerateBillSubRow, GenerateBillSubRowType } from '../../types';
import { isReconciledAgainstSalary, sumReconciledLineAmounts } from '../../utils';

/**
 * `useWatch` with an empty `name` array subscribes to the WHOLE form. Watching this inert path
 * instead keeps the subscription narrow for members that have no data sub-rows.
 */
const NO_WATCH_FIELD_PATHS = ['__noop__.hours'];

/** A selected sub-row reduced to the figures the member-level cells need. */
export interface SelectedSubRowLine {
  type: GenerateBillSubRowType;
  hours: number;
  rate: number;
}

export interface MemberSubRowFigures {
  selectedLines: SelectedSubRowLine[];
  totalHours: number;
  /** Project / added-project / time-off amounts the monthly salary absorbs. */
  reconciledTotal: number;
  /** Overhead amounts, which sit on top of the salary instead. */
  overheadTotal: number;
}

/**
 * Single source of truth for a member's live sub-row figures.
 *
 * Every member-level cell (hours, average rate, total) and the salary-adjustment cell need the
 * same thing: the currently-selected sub-rows with their *form* hours/rate rather than the
 * server snapshot. Computing it once here keeps those cells from drifting apart and avoids
 * repeating the filter → useWatch → reduce dance in each of them.
 */
export const useMemberSubRowFigures = (row: Row<GenerateBillListItem>): MemberSubRowFigures => {
  const formContext = useFormContext();

  // The add-overhead button row has no data, and the salary adjustment is derived from these
  // very figures — including it would make the calculation self-referential.
  const dataSubRows = row.subRows.filter(subRow => !isNonDataSubRow(subRow));

  const hoursFieldPaths = dataSubRows.map(subRow => getBillFieldName(subRow, 'hours'));
  const rateFieldPaths = dataSubRows.map(subRow => getBillFieldName(subRow, 'effectiveRate'));

  const hoursValues = useWatch({
    control: formContext.control,
    name: hoursFieldPaths.length > 0 ? hoursFieldPaths : NO_WATCH_FIELD_PATHS,
  });
  const rateValues = useWatch({
    control: formContext.control,
    name: rateFieldPaths.length > 0 ? rateFieldPaths : NO_WATCH_FIELD_PATHS,
  });

  // `getIsSelected()` is read outside React's reactive graph, so selection has to enter the
  // memo key explicitly — otherwise toggling a checkbox would leave these figures stale.
  const selectedRowIds = dataSubRows
    .filter(subRow => subRow.getIsSelected())
    .map(subRow => subRow.id)
    .join(',');

  return useMemo(() => {
    // Read watched values at the row's original `dataSubRows` index — filtering before mapping
    // would re-index against the filtered array and pull the wrong sub-row's hours/rate
    // (OPS-1584).
    const selectedLines = dataSubRows.reduce<SelectedSubRowLine[]>((acc, subRow, idx) => {
      if (!subRow.getIsSelected()) return acc;
      acc.push({
        type: (subRow.original as unknown as GenerateBillSubRow).type,
        hours: parseFormNumber(hoursValues?.[idx] ?? subRow.original.hours),
        rate: parseFormNumber(rateValues?.[idx] ?? subRow.original.effectiveRate),
      });
      return acc;
    }, []);

    let totalHours = 0;
    let overheadTotal = 0;

    selectedLines.forEach(({ type, hours, rate }) => {
      // Overheads are flat amounts carried on `rate`; they contribute no hours and sit on top
      // of the salary rather than being reconciled against it.
      if (type === GenerateBillSubRowType.OVERHEAD) {
        overheadTotal += rate;
        return;
      }
      totalHours += hours;
    });

    // Summed from each line's *rounded* amount, matching what the request mapper bills per line.
    // Summing raw products instead drifts by a fraction of a cent per line — see
    // `sumReconciledLineAmounts`.
    const reconciledTotal = sumReconciledLineAmounts(
      selectedLines.filter(({ type }) => isReconciledAgainstSalary(type))
    );

    return { selectedLines, totalHours, reconciledTotal, overheadTotal };
    // `selectedRowIds` stands in for the non-reactive `getIsSelected()` reads above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hoursValues, rateValues, selectedRowIds, dataSubRows.length]);
};
